<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="800px"
  >
    <!--<editor-fold desc="Dialog Header">-->
    <template slot="dialogTitle">
      <span v-if="contractorDirty.id">
        Edit Contractor
      </span>
      <span v-else>
        Add New Contractor
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Form">-->
    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-text-field
        v-model="contractorDirty.name"
        outlined
        label="Name"
        autocomplete="disable"
      />
      <v-autocomplete
        v-model="contractorDirty.country_id"
        outlined
        label="Country"
        :items="countries.data"
        :loading="countries.loading"
        item-value="id"
        item-text="name"
        autocomplete="disable"
      />
      <v-select
        v-model="contractorDirty.type"
        outlined
        label="Contractor Type"
        :items="contractorTypes"
        item-value="key"
        item-text="label"
      />
      <!--<editor-fold desc="Commissioning Yard Details">-->
      <div v-if="contractorDirty.type === TYPE_YARD">
        <v-subheader>
          Commissioning Yard Details
        </v-subheader>
        <v-row>
          <v-col>
            <v-text-field
              v-model="contractorDirty.trucking_duration"
              label="Trucking Duration"
              outlined
              type="number"
              step="1"
              min="0"
              hide-details
              suffix="days"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="contractorDirty.equipment_installing_duration"
              label="Equipment Installing Duration"
              outlined
              type="number"
              step="1"
              min="0"
              hide-details
              suffix="days"
            />
          </v-col>
        </v-row>
        <div v-if="contractorDirty.final_delivery_durations">
          <div class="strong ml-4 my-4">
            Final Delivery Durations
          </div>
          <v-row>
            <v-col
              v-for="finalDeliveryDuration in contractorDirty.final_delivery_durations"
              :key="`final-delivery-duration-${finalDeliveryDuration.country_id}`"
              cols="4"
            >
              <v-text-field
                v-model="finalDeliveryDuration.value"
                outlined
                type="number"
                step="1"
                min="0"
                hide-details
                suffix="days"
                :label="finalDeliveryDuration.country_name"
              />
            </v-col>
          </v-row>
        </div>
      </div>
      <!--</editor-fold>-->
      <!--<editor-fold desc="Contact Details">-->
      <v-subheader class="mt-5">
        Contact Details
      </v-subheader>
      <v-text-field
        v-model="contractorDirty.contact_details.address"
        outlined
        label="Address"
      />
      <v-text-field
        v-model="contractorDirty.contact_details.city"
        outlined
        label="City"
      />
      <v-text-field
        v-model="contractorDirty.contact_details.phone"
        outlined
        label="Telephone"
        placeholder="+385123123"
      />
      <v-text-field
        v-model="contractorDirty.contact_details.email"
        outlined
        label="Email"
        placeholder="email@email.com"
      />
      <v-row>
        <v-col>
          <v-text-field
            v-model="contractorDirty.contact_details.first_name"
            outlined
            label="Contact First Name"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="contractorDirty.contact_details.last_name"
            outlined
            label="Contact Last Name"
          />
        </v-col>
      </v-row>
      <!--</editor-fold>-->
      <!--<editor-fold desc="Additional Details">-->
      <v-subheader>
        Additional Details
      </v-subheader>
      <v-textarea
        v-model="contractorDirty.description"
        outlined
        label="Description"
        placeholder="Describe this contractor's services"
      />
      <!--</editor-fold>-->
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="contractorDirty.id && $can('delete', 'Contractor')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Contractor?"
      @confirmed="deleteContractor"
    />
  </content-dialog>
</template>

<script>
import { cloneDeep, keys, pick } from 'lodash'
import { mapState } from 'vuex'
import Contractor, { TYPE_YARD } from '@/models/Contractor'
import ContentDialog from '@/components/helpers/Dialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
    name: 'ContractorEditDialog',
    components: {
      ContentDialog,
      ConfirmDeleteDialog
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      contractor: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        contractorDirty: cloneDeep(Contractor.emptyModel),
        submitting: false,
        contractorTypes: Contractor.typeData
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      charteringCountries: function() {
        return this.$store.getters['settings/charteringCountries']
      },
      ...mapState('settings', ['countries'])
    },
    watch: {
      contractor: function(newContractor) {
        this.contractorDirty = cloneDeep(newContractor)
        this.prepareFinalDeliveryDurations()
      }
    },
    created() {
      this.TYPE_YARD = TYPE_YARD
      this.prepareFinalDeliveryDurations()
    },
    methods: {
      async submit() {
        // TODO: Add validation
        let result = await this.$validator.validate()

        if (!result) return

        this.save()
      },
      async save() {
        this.submitting = true

        const data = {
          contact_details: {
            address: this.contractorDirty.contact_details.address,
            city: this.contractorDirty.contact_details.city,
            phone: this.contractorDirty.contact_details.phone,
            email: this.contractorDirty.contact_details.email,
            first_name: this.contractorDirty.contact_details.first_name,
            last_name: this.contractorDirty.contact_details.last_name
          },
          country_id: this.contractorDirty.country_id,
          description: this.contractorDirty.description,
          name: this.contractorDirty.name,
          type: this.contractorDirty.type,
          yacht_manufacturer_id: this.contractorDirty.yacht_manufacturer_id,
          trucking_duration: this.contractorDirty.trucking_duration,
          equipment_installing_duration: this.contractorDirty.equipment_installing_duration,
          final_delivery_durations: this.contractorDirty.final_delivery_durations
        }

        try {
          let contractor = await Contractor.api.save(data, this.contractorDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Contractor saved',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.contractorDirty.id ? 'contractor:updated' : 'contractor:created', contractor)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        } finally {
          this.submitting = false
        }
      },
      async deleteContractor() {
        this.deleting = true

        try {
          await Contractor.api.destroy(this.contractorDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Contractor deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('contractor:deleted', this.contractorDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      },
      getParams() {
        return pick(this.contractorDirty, keys(Contractor.emptyModel))
      },
      prepareFinalDeliveryDurations: function() {
        let currentFinalDeliveryDurations = this.contractorDirty.final_delivery_durations || []
        let finalDeliveryDurationsCountryIds = currentFinalDeliveryDurations.map(c => c.country_id)

        // adding new chartering countries
        let newCharteringCountries = this.charteringCountries.data
          .filter(charteringCountry => finalDeliveryDurationsCountryIds.indexOf(charteringCountry.id) < 0)
          .map(country => ({
            country_name: country.name,
            country_id: country.id,
            value: null
          }))

        this.contractorDirty.final_delivery_durations = [
          ...currentFinalDeliveryDurations,
          ...newCharteringCountries
        ]
      }
    }
  }
</script>
